import React, { useState, useEffect } from 'react';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { apiService } from '../../../../../services/api';
import './WeatherChart.css';

const WeatherChart = ({ serial }) => {
  const [weatherData, setWeatherData] = useState(null);
  const [uvData, setUvData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(30); // days

  useEffect(() => {
    if (serial) {
      fetchWeatherAnalytics();
    }
  }, [serial, timeRange]);

  const fetchWeatherAnalytics = async () => {
    if (!serial) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date(endDate.getTime() - (timeRange * 24 * 60 * 60 * 1000));
      
      // Fetch daily weather analytics using the correct blockApi method
      const response = await apiService.callFunction('blockOperations', {
        operation: 'getDailyWeatherAnalytics',
        serial,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        limit: timeRange
      }, { identifier: serial });

      if (response.success) {
        const { dailyWeather, dailyUV } = response.data;
        setWeatherData(dailyWeather);
        setUvData(dailyUV);
      } else {
        throw new Error(response.message || 'Failed to fetch weather analytics');
      }
    } catch (err) {
      console.error('Error fetching weather analytics:', err);
      setError(err.message);
      setWeatherData([]);
      setUvData([]);
    } finally {
      setLoading(false);
    }
  };

  // Weather condition color mapping
  const getWeatherColor = (condition) => {
    const colors = {
      'Clear': '#FFD700',
      'Sunny': '#FFA500', 
      'Clouds': '#87CEEB',
      'Rain': '#4682B4',
      'Drizzle': '#6495ED',
      'Thunderstorm': '#483D8B',
      'Snow': '#F0F8FF',
      'Mist': '#D3D3D3',
      'Fog': '#A9A9A9',
      'Haze': '#F5DEB3'
    };
    return colors[condition] || '#95a5a6';
  };

  // UV Index color coding
  const getUVColor = (uvIndex) => {
    if (uvIndex <= 2) return '#289500'; // Low (Green)
    if (uvIndex <= 5) return '#F7E400'; // Moderate (Yellow)
    if (uvIndex <= 7) return '#F85900'; // High (Orange)
    if (uvIndex <= 10) return '#D90011'; // Very High (Red)
    return '#6B49C8'; // Extreme (Violet)
  };

  // Prepare chart data
  const prepareChartData = () => {
    if (!weatherData || !uvData) return [];
    
    // Merge weather and UV data by date
    const mergedData = [];
    const dateMap = new Map();
    
    // Add weather data
    weatherData.forEach(item => {
      dateMap.set(item.date, {
        date: item.date,
        weather: item.weather,
        weatherCount: item.count,
        displayDate: new Date(item.date).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        })
      });
    });
    
    // Add UV data
    uvData.forEach(item => {
      const existing = dateMap.get(item.date) || {
        date: item.date,
        displayDate: new Date(item.date).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        })
      };
      
      dateMap.set(item.date, {
        ...existing,
        avgUV: item.avgUV,
        maxUV: item.maxUV,
        minUV: item.minUV,
        uvReadings: item.readings
      });
    });
    
    // Convert to array and sort by date
    return Array.from(dateMap.values())
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-timeRange); // Show most recent data
  };

  const chartData = prepareChartData();

  if (loading) {
    return (
      <div className="weather-chart-loading">
        <div className="loading-spinner"></div>
        <p>Loading weather analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="weather-chart-error">
        <p>⚠️ Error loading weather data: {error}</p>
        <button onClick={fetchWeatherAnalytics} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="weather-chart-container">
      <div className="chart-controls">
        <label>Time Range:</label>
        <select 
          value={timeRange} 
          onChange={(e) => setTimeRange(parseInt(e.target.value))}
          className="time-range-select"
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 2 weeks</option>
          <option value={30}>Last month</option>
          <option value={60}>Last 2 months</option>
        </select>
        <button onClick={fetchWeatherAnalytics} className="refresh-button">
          🔄 Refresh
        </button>
      </div>

      {chartData.length === 0 ? (
        <div className="no-weather-data">
          <p>📊 No weather data available for the selected time range.</p>
          <p>Weather data is collected during dispense events.</p>
        </div>
      ) : (
        <>
          {/* UV Index Chart */}
          <div className="chart-section">
            <h4>📈 Daily UV Index Averages</h4>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="displayDate" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  domain={[0, 12]}
                  label={{ value: 'UV Index', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  formatter={(value, name) => [
                    `${value} UV`, 
                    name === 'avgUV' ? 'Average UV' : 
                    name === 'maxUV' ? 'Max UV' : 'Min UV'
                  ]}
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <Legend />
                
                {/* UV Index reference lines */}
                <ReferenceLine y={2} stroke="#289500" strokeDasharray="2 2" label="Low" />
                <ReferenceLine y={5} stroke="#F7E400" strokeDasharray="2 2" label="Moderate" />
                <ReferenceLine y={7} stroke="#F85900" strokeDasharray="2 2" label="High" />
                <ReferenceLine y={10} stroke="#D90011" strokeDasharray="2 2" label="Very High" />
                
                <Line 
                  type="monotone" 
                  dataKey="avgUV" 
                  stroke="#e74c3c" 
                  strokeWidth={3}
                  dot={{ fill: '#e74c3c', strokeWidth: 2, r: 4 }}
                  name="Average UV"
                />
                <Line 
                  type="monotone" 
                  dataKey="maxUV" 
                  stroke="#c0392b" 
                  strokeWidth={1}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Max UV"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Weather Conditions Chart */}
          <div className="chart-section">
            <h4>🌤️ Daily Weather Conditions</h4>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="displayDate" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  label={{ value: 'Occurrences', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value} times`, 
                    `${props.payload.weather || 'No data'}`
                  ]}
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <Bar 
                  dataKey="weatherCount" 
                  fill={(entry) => getWeatherColor(entry?.weather)}
                  name="Weather Occurrences"
                />
              </BarChart>
            </ResponsiveContainer>
            
            {/* Weather Legend */}
            <div className="weather-legend">
              <h5>Weather Conditions Legend:</h5>
              <div className="legend-items">
                {Array.from(new Set(chartData.filter(d => d.weather).map(d => d.weather))).map(condition => (
                  <div key={condition} className="legend-item">
                    <div 
                      className="legend-color" 
                      style={{ backgroundColor: getWeatherColor(condition) }}
                    ></div>
                    <span>{condition}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Summary Statistics */}
          <div className="weather-summary">
            <h4>📊 Summary Statistics</h4>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="label">Days with Data:</span>
                <span className="value">{chartData.filter(d => d.weather || d.avgUV).length}</span>
              </div>
              <div className="summary-item">
                <span className="label">Avg UV (Period):</span>
                <span className="value">
                  {chartData.filter(d => d.avgUV).length > 0 
                    ? (chartData.filter(d => d.avgUV).reduce((sum, d) => sum + d.avgUV, 0) / 
                       chartData.filter(d => d.avgUV).length).toFixed(1)
                    : 'N/A'
                  }
                </span>
              </div>
              <div className="summary-item">
                <span className="label">Most Common Weather:</span>
                <span className="value">
                  {(() => {
                    const weatherData = chartData.filter(d => d.weather);
                    if (weatherData.length === 0) return 'N/A';
                    
                    const counts = weatherData.reduce((acc, curr) => {
                      acc[curr.weather] = (acc[curr.weather] || 0) + curr.weatherCount;
                      return acc;
                    }, {});
                    
                    const mostCommon = Object.entries(counts).sort(([,a], [,b]) => b - a)[0];
                    return mostCommon ? mostCommon[0] : 'N/A';
                  })()}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WeatherChart;
